import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface PublisherCreationFormData {
  // Publisher Details
  publisherName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  ddi?: string;
  ddiCountryCode?: string; // Store country code for flag restoration
  phoneMask?: string; // Store phone mask for input restoration
  phoneNumber?: string;
  birthDate?: string;

  // Publisher Access (excluding sensitive password data)
  username?: string;

  // Form state
  currentStep?: string;
}

export interface PublisherFormControlStates {
  // Track which fields have been interacted with
  publisherName?: { touched: boolean; dirty: boolean };
  firstName?: { touched: boolean; dirty: boolean };
  lastName?: { touched: boolean; dirty: boolean };
  email?: { touched: boolean; dirty: boolean };
  ddi?: { touched: boolean; dirty: boolean };
  phoneNumber?: { touched: boolean; dirty: boolean };
  birthDate?: { touched: boolean; dirty: boolean };
  username?: { touched: boolean; dirty: boolean };
}

@Injectable({
  providedIn: 'root',
})
export class MzicPublisherCreationFormStateService {
  private publisherImageSource = new BehaviorSubject<string | null>(null);
  public publisherImage$ = this.publisherImageSource.asObservable();

  private imageIdSource = new BehaviorSubject<string | null>(null);
  public imageId$ = this.imageIdSource.asObservable();

  private publisherFormDataSource =
    new BehaviorSubject<PublisherCreationFormData>({});
  public publisherFormData$ = this.publisherFormDataSource.asObservable();

  private currentStepSource = new BehaviorSubject<string>('initial');
  public currentStep$ = this.currentStepSource.asObservable();

  private controlStatesSource = new BehaviorSubject<PublisherFormControlStates>(
    {},
  );
  public controlStates$ = this.controlStatesSource.asObservable();

  constructor() {
    this.initStateFromStorage();
  }

  private initStateFromStorage(): void {
    try {
      const savedImage = localStorage.getItem('publisher_creation_form_image');
      const savedImageId = localStorage.getItem(
        'publisher_creation_form_image_id',
      );
      const savedFormData = localStorage.getItem(
        'publisher_creation_form_data_new',
      );
      const savedStep = localStorage.getItem('publisher_creation_form_step');
      const savedControlStates = localStorage.getItem(
        'publisher_creation_form_control_states',
      );

      if (savedImage) {
        this.publisherImageSource.next(savedImage);
      }

      if (savedImageId) {
        this.imageIdSource.next(savedImageId);
      }

      if (savedFormData) {
        this.publisherFormDataSource.next(JSON.parse(savedFormData));
      }

      if (savedStep) {
        this.currentStepSource.next(savedStep);
      }

      if (savedControlStates) {
        this.controlStatesSource.next(JSON.parse(savedControlStates));
      }
    } catch (error) {
      console.error(
        'Error recovering publisher form state from localStorage',
        error,
      );
      this.clearState();
    }
  }

  setPublisherImage(imageUrl: string): void {
    this.publisherImageSource.next(imageUrl);
    localStorage.setItem('publisher_creation_form_image', imageUrl);
  }

  setImageId(imageId: string): void {
    this.imageIdSource.next(imageId);
    localStorage.setItem('publisher_creation_form_image_id', imageId);
  }

  getPublisherImage(): string | null {
    return this.publisherImageSource.value;
  }

  getImageId(): string | null {
    return this.imageIdSource.value;
  }

  updatePublisherFormData(data: Partial<PublisherCreationFormData>): void {
    const currentData = this.publisherFormDataSource.value;
    const updatedData = { ...currentData, ...data };
    this.publisherFormDataSource.next(updatedData);
    localStorage.setItem(
      'publisher_creation_form_data_new',
      JSON.stringify(updatedData),
    );
  }

  getPublisherFormData(): PublisherCreationFormData {
    return this.publisherFormDataSource.value;
  }

  setCurrentStep(step: string): void {
    this.currentStepSource.next(step);
    localStorage.setItem('publisher_creation_form_step', step);
  }

  getCurrentStep(): string {
    return this.currentStepSource.value;
  }

  updateControlStates(states: Partial<PublisherFormControlStates>): void {
    const currentStates = this.controlStatesSource.value;
    const updatedStates = { ...currentStates, ...states };
    this.controlStatesSource.next(updatedStates);
    localStorage.setItem(
      'publisher_creation_form_control_states',
      JSON.stringify(updatedStates),
    );
  }

  getControlStates(): PublisherFormControlStates {
    return this.controlStatesSource.value;
  }

  clearState(): void {
    this.publisherImageSource.next(null);
    this.imageIdSource.next(null);
    this.publisherFormDataSource.next({});
    this.currentStepSource.next('initial');
    this.controlStatesSource.next({});
    localStorage.removeItem('publisher_creation_form_image');
    localStorage.removeItem('publisher_creation_form_image_id');
    localStorage.removeItem('publisher_creation_form_data_new');
    localStorage.removeItem('publisher_creation_form_step');
    localStorage.removeItem('publisher_creation_form_control_states');
  }

  /**
   * Clears all registration data when going back from profile setup (first step)
   * This provides a complete fresh start for the registration process
   */
  clearNameAndImage(): void {
    // Since profile setup is the first step, clear ALL registration data
    // This ensures users get a completely fresh start when going back
    this.clearState();
  }

  hasPersistedData(): boolean {
    const formData = this.getPublisherFormData();
    return Object.keys(formData).length > 0 || !!this.getPublisherImage();
  }
}
